import { ArrowLeft, FileSignature, FileText, Landmark, Shield } from "lucide-react";
import termsText from "../../legal/terms.txt?raw";
import privacyText from "../../legal/privacy.txt?raw";
import contractText from "../../legal/contract.txt?raw";
import type { RoseLegalTab } from "../App";

interface TermsPrivacyProps {
  activeTab?: RoseLegalTab;
  onTabChange?: (tab: RoseLegalTab) => void;
  onBackToHome: () => void;
}

const legalDocs: Record<RoseLegalTab, { label: string; title: string; icon: typeof FileText; text: string }> = {
  terms: {
    label: "Terms",
    title: "Terms & Conditions",
    icon: FileText,
    text: termsText,
  },
  privacy: {
    label: "Privacy",
    title: "Privacy Policy",
    icon: Shield,
    text: privacyText,
  },
  contract: {
    label: "Contract",
    title: "Service Contract",
    icon: FileSignature,
    text: contractText,
  },
};

export default function TermsPrivacy({ activeTab = "terms", onTabChange, onBackToHome }: TermsPrivacyProps) {
  const activeDoc = legalDocs[activeTab] || legalDocs.terms;

  return (
    <article className="min-h-screen bg-[#F5F2ED] text-[#1A1A1A] py-24 px-4 sm:px-6 relative border-t border-[#1A1A1A]">
      <div className="grain-overlay absolute inset-0 z-0 opacity-15 pointer-events-none"></div>

      <div className="max-w-4xl mx-auto relative z-10 space-y-12">
        <div className="space-y-6">
          <button
            onClick={onBackToHome}
            className="group flex items-center space-x-2 text-xs uppercase tracking-[0.25em] font-sans font-medium text-neutral-500 hover:text-black transition duration-300"
          >
            <ArrowLeft size={14} className="transform group-hover:-translate-x-1 transition-transform" />
            <span>Return Home</span>
          </button>

          <div className="border-b border-[#1A1A1A]/10 pb-6">
            <span className="text-[10px] tracking-[0.35em] uppercase text-[#c4a482] font-semibold flex items-center gap-1.5 mb-3">
              <Landmark size={11} /> Legal Protocol
            </span>
            <h1 className="font-serif text-[38px] sm:text-[52px] font-extralight tracking-tight leading-none text-[#1A1A1A]">
              {activeDoc.title}
            </h1>
            <p className="font-sans text-[10px] uppercase tracking-widest text-[#7f7d78] mt-3 font-light">
              Love & Photos LLC operating under The Rose Photo
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-[#1A1A1A]/10">
          {(Object.keys(legalDocs) as RoseLegalTab[]).map((tab) => {
            const doc = legalDocs[tab];
            const Icon = doc.icon;
            const isActive = activeTab === tab;

            return (
              <button
                key={tab}
                onClick={() => onTabChange?.(tab)}
                className={`flex items-center space-x-2 pb-4 px-4 sm:px-6 text-xs uppercase tracking-widest font-sans font-semibold transition-all border-b-2 cursor-pointer ${
                  isActive
                    ? "border-[#1A1A1A] text-[#1A1A1A]"
                    : "border-transparent text-neutral-400 hover:text-neutral-600"
                }`}
              >
                <Icon size={14} />
                <span>{doc.label}</span>
              </button>
            );
          })}
        </div>

        <div className="bg-white/50 border border-[#1A1A1A]/5 p-5 sm:p-10 shadow-sm">
          <pre className="whitespace-pre-wrap break-words font-serif text-[13px] sm:text-[14px] leading-relaxed text-neutral-700">
            {activeDoc.text}
          </pre>
        </div>
      </div>
    </article>
  );
}
